/**
 * ReferencePreview — the floating wrapper for a reference's hover card.
 * Positions itself above or below its target; put a ReferencePreviewCard
 * inside it. Guidelines: Storybook, Compositions/Analysis/Reference Preview.
 */

import { type ReactNode, type RefObject, useLayoutEffect, useRef, useState } from 'react';
import './ReferencePreview.css';

export type PreviewPlacement = 'above' | 'below';

export interface ReferencePreviewProps {
  /** Reference to the target element to position relative to */
  targetRef: RefObject<HTMLElement | null>;
  /** Placement relative to target */
  placement: PreviewPlacement;
  /** Whether the preview is visible */
  visible: boolean;
  /** Mouse enter handler (for hover bridge) */
  onMouseEnter?: () => void;
  /** Mouse leave handler (for hover bridge) */
  onMouseLeave?: () => void;
  /** Preview content */
  children: ReactNode;
}

interface Position {
  top: number;
  left: number;
}

/**
 * Calculate the position for the preview card.
 */
function calculatePosition(
  targetRect: DOMRect,
  previewRect: DOMRect,
  placement: PreviewPlacement
): Position {
  const viewportWidth = window.innerWidth;
  const padding = 16;

  // Horizontal: center on target, clamped to viewport
  let left = targetRect.left + targetRect.width / 2 - previewRect.width / 2;
  left = Math.max(padding, Math.min(left, viewportWidth - previewRect.width - padding));

  // Vertical: 8px gap from target
  let top: number;
  if (placement === 'above') {
    top = targetRect.top - previewRect.height - 8;
  } else {
    top = targetRect.bottom + 8;
  }

  return { top, left };
}

export function ReferencePreview({
  targetRef,
  placement,
  visible,
  onMouseEnter,
  onMouseLeave,
  children,
}: ReferencePreviewProps) {
  const previewRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<Position>({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!visible || !targetRef.current || !previewRef.current) return;

    const targetRect = targetRef.current.getBoundingClientRect();
    const previewRect = previewRef.current.getBoundingClientRect();
    const newPosition = calculatePosition(targetRect, previewRect, placement);
    setPosition(newPosition);
  }, [visible, targetRef, placement, children]);

  const className = [
    'ref-preview',
    visible ? 'ref-preview--visible' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      ref={previewRef}
      className={className}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>
  );
}

export default ReferencePreview;
